import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { getMe } from '../../modules/admin/api';
import type { Me } from '../../modules/admin/types';
import { isAuthenticated, logout } from '../auth/okta';
import { isAuthEnabled } from '../utils/auth';

export type MeContextValue = {
  me: Me | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
};

const MeContext = createContext<MeContextValue | null>(null);

/**
 * Fetches /api/me once for the subtree. Wrap authenticated app routes only.
 */
export function MeProvider({ children }: { children: React.ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMe = useCallback(async () => {
    if (isAuthEnabled() && !isAuthenticated()) {
      setMe(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await getMe();
      setMe(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load user');
      setMe(null);
      logout();
      window.location.href = '/';
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const value = useMemo(
    () => ({
      me,
      loading,
      error,
      refetch: fetchMe,
    }),
    [me, loading, error, fetchMe]
  );

  return <MeContext.Provider value={value}>{children}</MeContext.Provider>;
}

export function useMe(): MeContextValue {
  const ctx = useContext(MeContext);
  if (!ctx) {
    throw new Error('useMe must be used within MeProvider');
  }
  return ctx;
}
